import React, { useCallback, useEffect } from 'react';
import { Drawer } from 'antd';
import { XIcon } from '@phosphor-icons/react';
import { DashboardAiDesignsTabChrome } from './DashboardAiDesignsTabChrome';
import { DashboardAiSlidePanelChrome } from './DashboardAiSlidePanelChrome';

interface DashboardAiRailSectionProps {
    aiRailOpen: boolean;
    setAiRailOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    setTreeCuttingPopupOpen: (next: boolean) => void;
    setTreeCuttingForLayoutKey: (next: string | null) => void;
    isDashboardOpen: boolean;
    theme: 'dark' | 'light';
    aiPanelTab: 'ai' | 'tree' | 'saved' | 'wishlist';
    setAiPanelTab: (next: 'ai' | 'tree' | 'saved' | 'wishlist') => void;
    aiLoading?: boolean;
    children: React.ReactNode;
}

export const DashboardAiRailSection: React.FC<DashboardAiRailSectionProps> = ({
    aiRailOpen,
    setAiRailOpen,
    setTreeCuttingPopupOpen,
    setTreeCuttingForLayoutKey,
    isDashboardOpen,
    theme,
    aiPanelTab,
    setAiPanelTab,
    aiLoading = false,
    children,
}) => {
    const drawerZIndex = isDashboardOpen ? 10070 : aiLoading ? 500 : 1000;
    const collapseRail = useCallback(() => {
        setAiRailOpen(false);
        setTreeCuttingPopupOpen(false);
        setTreeCuttingForLayoutKey(null);
    }, [setAiRailOpen, setTreeCuttingPopupOpen, setTreeCuttingForLayoutKey]);

    // Close the rail when the user clicks anywhere outside its drawer surface.
    // In viewer mode the drawer is mask-less, so without this listener clicks on
    // the 3D viewer never reach a close affordance.
    useEffect(() => {
        if (!aiRailOpen) return;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            // Keep the rail open when interacting with the drawer surface itself
            // (content, header, or its AntD wrapper) or with the peek button used
            // to re-open it.
            if (target.closest('.obd-ai-drawer')) return;
            if (target.closest('.obd-ai-drawer-root')) return;
            if (target.closest('[data-ai-rail-keepalive="true"]')) return;
            if (target.closest('.sidebar')) return;
            if (target.closest('.menubar-designs-ai-btn')) return;

            // Don't dismiss when a popup/modal/dropdown/tooltip launched from the
            // rail receives the click — these portal outside the drawer DOM.
            const overlaySelectors = [
                '.ant-modal-root',
                '.ant-modal-wrap',
                '.ant-modal',
                '.ant-popover',
                '.ant-tooltip',
                '.ant-dropdown',
                '.ant-select-dropdown',
                '.ant-picker-dropdown',
                '.ant-message',
                '.ant-notification',
            ];
            if (overlaySelectors.some((sel) => target.closest(sel))) return;

            collapseRail();
        };

        // Defer so the opening click (menubar / peek) cannot immediately dismiss the rail.
        const timerId = window.setTimeout(() => {
            document.addEventListener('mousedown', handlePointerDown, true);
        }, 0);
        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener('mousedown', handlePointerDown, true);
        };
    }, [aiRailOpen, collapseRail]);

    return (
        <Drawer
            open={aiRailOpen}
            onClose={collapseRail}
            placement="right"
            zIndex={drawerZIndex}
            getContainer={() => document.body}
            rootClassName={`obd-ai-drawer-root obd-theme-${theme}${isDashboardOpen ? ' obd-ai-drawer-root--dashboard-open' : ''}${aiLoading ? ' obd-ai-drawer-root--ai-loading' : ''}`}
            width={!isDashboardOpen ? 330 : '50%'}
            mask={isDashboardOpen}
            keyboard
            closable={false}
            className={`obd-ai-drawer obd-theme-${theme}${isDashboardOpen ? ' obd-ai-drawer--dashboard-open' : ''}`}
            classNames={{ body: 'obd-ai-drawer__body' }}
            title={null}
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
        >
            <div className={`obd-ai-rail__chrome${!isDashboardOpen ? ' obd-ai-rail__chrome--viewer' : ''}`}>
                {!isDashboardOpen ? (
                    <div className="obd-ai-rail__viewer-chrome obd-ai-rail__viewer-chrome--icons-only">
                        <div className="obd-ai-rail__viewer-bar obd-ai-rail__viewer-bar--icons-only">
                            <DashboardAiSlidePanelChrome
                                aiPanelTab={aiPanelTab}
                                setAiPanelTab={setAiPanelTab}
                                iconsOnly
                            />
                            <button
                                type="button"
                                className="obd-ai-rail__collapse-edge obd-ai-rail__collapse-edge--close"
                                onClick={collapseRail}
                                aria-label="Close designs panel"
                                title="Close designs panel"
                            >
                                <XIcon size={18} weight="bold" aria-hidden />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="obd-ai-rail__chrome-header-row">
                        <DashboardAiDesignsTabChrome aiPanelTab={aiPanelTab} setAiPanelTab={setAiPanelTab} />
                        <button
                            type="button"
                            className="obd-ai-rail__collapse-edge obd-ai-rail__collapse-edge--dashboard obd-ai-rail__collapse-edge--close"
                            onClick={collapseRail}
                            aria-label="Close designs panel"
                            title="Close designs panel"
                        >
                            <XIcon size={18} weight="bold" aria-hidden />
                        </button>
                    </div>
                )}
            </div>
            <div className="obd-ai-rail__body">{children}</div>
        </Drawer>
    );
};
